// frontend/src/components/HoopsAnalyst.js
import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  LogOut,
  Menu,
  X,
  User,
  Zap,
  Settings,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import PlayerSearch from './PlayerSearch';
import PlayerPerformance from './PlayerPerformance';
import AccountSettings from './AccountSettings';
import { getCurrentSeason } from '../services/nbaApi';


// Converts a player name to a URL-safe slug, e.g. "LeBron James" → "lebron-james"
function toPlayerSlug(firstName, lastName) {
  return `${firstName}-${lastName}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

const HoopsAnalyst = () => {
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [recentSearches, setRecentSearches] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const { playerSlug } = useParams();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const season = getCurrentSeason();

  // Flag to suppress the slug-load effect when we just navigated programmatically
  const skipSlugEffect = useRef(false);

  const handleSelectPlayer = (player) => {
    skipSlugEffect.current = true;
    setSelectedPlayer(player);
    const slug = toPlayerSlug(player.first_name, player.last_name);
    navigate(`/app/${slug}`, { replace: true });
    setRecentSearches((prev) => {
      const filtered = prev.filter((p) => p.id !== player.id);
      return [player, ...filtered].slice(0, 8);
    });
  };

  // Load the player whenever the URL slug changes (including on first mount)
  useEffect(() => {
    if (skipSlugEffect.current) {
      skipSlugEffect.current = false;
      return;
    }

    if (!playerSlug) {
      // No slug — check legacy sessionStorage handoff from the landing page
      try {
        const stored = sessionStorage.getItem('hoops_selected_player');
        if (stored) {
          const player = JSON.parse(stored);
          sessionStorage.removeItem('hoops_selected_player');
          handleSelectPlayer(player);
        }
      } catch (_) {}
      return;
    }

    // Slug present — load from static player JSON
    fetch('/nba-players.json', { cache: 'no-store' })
      .then((r) => r.json())
      .then(({ players = [] }) => {
        const found = players.find(
          (p) => toPlayerSlug(p.first_name, p.last_name) === playerSlug,
        );
        if (found) {
          const player = {
            ...found,
            // Normalise team shape so components that expect player.team.abbreviation work
            team: { id: found.team_id, abbreviation: found.team_abbr },
          };
          setSelectedPlayer(player);
          setRecentSearches((prev) => {
            const filtered = prev.filter((p) => p.id !== player.id);
            return [player, ...filtered].slice(0, 8);
          });
        }
      })
      .catch(() => {});
  }, [playerSlug]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogout = async () => {
    await signOut();
  };

  const apiKeyConfigured = !!process.env.REACT_APP_BDL_API_KEY;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800">
      {/* Account Settings modal */}
      {settingsOpen && <AccountSettings onClose={() => setSettingsOpen(false)} />}

      {/* Header */}
      <header className="bg-gray-900/50 backdrop-blur-xl border-b border-gray-700/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <a href="/" className="flex items-center hover:opacity-80 transition-opacity">
              <BarChart3 className="h-8 w-8 text-orange-500 mr-3" />
              <h1 className="text-2xl font-black text-white">
                Hoops<span className="text-orange-500">Analyst</span>
              </h1>
            </a>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="/"
                className="text-gray-300 hover:text-orange-500 transition-colors"
              >
                Rankings
              </a>
              <a href="/app" className="text-orange-500 font-medium">
                Analytics
              </a>
            </nav>

            {/* User Menu */}
            <div className="flex items-center space-x-3">
              <span className="text-gray-300 text-sm hidden sm:block">
                {user?.email}
              </span>
              <button
                onClick={() => setSettingsOpen(true)}
                className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white px-3 py-2 rounded-lg transition-colors"
                title="Account Settings"
              >
                <Settings className="h-4 w-4" />
                <span className="hidden sm:block">Account</span>
              </button>
              <button
                onClick={handleLogout}
                className="flex items-center space-x-2 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:block">Logout</span>
              </button>

              {/* Mobile menu button */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="md:hidden text-gray-300 hover:text-white"
              >
                {sidebarOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* API Key Warning */}
        {!apiKeyConfigured && (
          <div className="mb-6 bg-yellow-900/30 border border-yellow-600/50 rounded-xl p-4 flex items-start gap-3">
            <Zap className="h-5 w-5 text-yellow-400 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-yellow-200 font-medium">API key not configured</p>
              <p className="text-yellow-300/70 text-sm mt-1">
                Add <code className="bg-yellow-800/50 px-1 rounded">REACT_APP_BDL_API_KEY</code> to
                your <code className="bg-yellow-800/50 px-1 rounded">.env</code> file with your
                free BallDontLie API key from{' '}
                <a
                  href="https://app.balldontlie.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-yellow-100"
                >
                  app.balldontlie.io
                </a>
              </p>
            </div>
          </div>
        )}

        {/* Search Section */}
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-white mb-2">Player Analytics</h2>
          <p className="text-gray-400 mb-6">
            Search any NBA player to compare their recent performance against season
            averages ({season}-{(season + 1).toString().slice(2)})
          </p>

          <PlayerSearch
            onSelectPlayer={handleSelectPlayer}
            className="max-w-2xl"
          />
        </div>

        {/* Recent Searches */}
        {recentSearches.length > 0 && (
          <div className="mb-8">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
              Recent Searches
            </h3>
            <div className="flex flex-wrap gap-2">
              {recentSearches.map((p) => {
                const teamAbbr = p.team?.abbreviation || '—';
                return (
                  <button
                    key={p.id}
                    onClick={() => handleSelectPlayer(p)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      selectedPlayer?.id === p.id
                        ? 'bg-orange-500 text-white'
                        : 'bg-gray-800/50 hover:bg-gray-700/50 border border-gray-600/50 text-gray-200'
                    }`}
                  >
                    <span className="font-semibold">{p.first_name} {p.last_name}</span>
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${
                        selectedPlayer?.id === p.id
                          ? 'bg-orange-600 text-orange-100'
                          : 'bg-gray-700 text-gray-300'
                      }`}
                    >
                      {teamAbbr}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Player Performance */}
        {selectedPlayer ? (
          <PlayerPerformance player={selectedPlayer} />
        ) : (
          /* Empty State */
          <div className="bg-gray-800/50 rounded-2xl p-12 border border-gray-700/50 text-center">
            <User className="h-16 w-16 text-gray-500 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-white mb-2">
              No Player Selected
            </h3>
            <p className="text-gray-400 mb-6">
              Search for an NBA player to compare their recent performance
              (last 5, 10, 20 games) against full season averages
            </p>
            <div className="flex flex-wrap justify-center gap-2">
              <span className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm">
                Rolling Averages
              </span>
              <span className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm">
                Trend Analysis
              </span>
              <span className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm">
                Game Logs
              </span>
              <span className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm">
                Performance Comparison
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HoopsAnalyst;
